import React, { useState } from 'react';
import { useSettings } from '../App';
import Icon from '../components/Icon';
import './RechercheCorpo.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const MOIS = ['janvier','février','mars','avril','mai','juin','juillet',
              'août','septembre','octobre','novembre','décembre'];

function fmtDate(str) {
  if (!str) return '—';
  const d = new Date(str + 'T12:00:00');
  return `${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtMontant(n) {
  if (n == null || n === 0) return '0,00 $';
  return `${Number(n).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
}

const BADGE_STATUT = {
  en_construction: { label: 'En préparation',          classe: 'badge-gris'  },
  envoye:          { label: 'En attente de signature',  classe: 'badge-bleu'  },
  en_revision:     { label: 'En révision',              classe: 'badge-bleu'  },
  accepte:         { label: 'Confirmé ✓',               classe: 'badge-vert'  },
  complete:        { label: 'Complété',                 classe: 'badge-muted' },
  termine:         { label: 'Complété',                 classe: 'badge-muted' },
  annule:          { label: 'Annulé',                   classe: 'badge-danger'},
  refuse:          { label: 'Refusé',                   classe: 'badge-danger'},
};

function ContratCard({ contrat }) {
  const badge = BADGE_STATUT[contrat.statut] || { label: contrat.statut, classe: 'badge-gris' };

  return (
    <div className="rc-contrat-card">
      <div className="rc-contrat-header">
        <span className={`rc-badge ${badge.classe}`}>{badge.label}</span>
        <span className="rc-contrat-nom">{contrat.nom_evenement}</span>
      </div>

      <div className="rc-contrat-meta">
        <span className="rc-meta-item">
          <Icon name="date" size={13} color="var(--muted)" />
          {fmtDate(contrat.date_evenement)}
        </span>
        {contrat.nb_personnes && (
          <span className="rc-meta-item">
            <Icon name="utilisateur" size={13} color="var(--muted)" />
            {contrat.nb_personnes} personnes
          </span>
        )}
        {contrat.lieu && (
          <span className="rc-meta-item">
            <Icon name="lieu" size={13} color="var(--muted)" />
            {contrat.lieu}
          </span>
        )}
      </div>

      <div className="rc-contrat-montants">
        <div className="rc-montant-ligne">
          <span>Total</span>
          <span>{fmtMontant(contrat.montant_total)}</span>
        </div>
        <div className="rc-montant-ligne">
          <span>Acompte versé</span>
          <span>{fmtMontant(contrat.acompte_verse)}</span>
        </div>
        <div className="rc-montant-ligne rc-montant-solde">
          <span>Solde restant</span>
          <span className="rc-solde-montant">{fmtMontant(contrat.solde_restant)}</span>
        </div>
      </div>

      <div className="rc-contrat-footer">
        {contrat.token_acces ? (
          <a
            href={`/contrat/${contrat.token_acces}`}
            className="rc-btn-primary"
          >
            Voir ma soumission →
          </a>
        ) : (
          <a href="mailto:info@escamel.ca" className="rc-btn-ghost">
            Contactez-nous
          </a>
        )}
      </div>
    </div>
  );
}

export default function RechercheCorpo() {
  const settings = useSettings();

  const [form, setForm] = useState({ nom_famille: '', tel_4_derniers: '', courriel: '' });
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState(null);

  function handleChange(e) {
    const { name, value } = e.target;
    if (name === 'tel_4_derniers') {
      const chiffres = value.replace(/\D/g, '').slice(0, 4);
      setForm(prev => ({ ...prev, tel_4_derniers: chiffres }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErreur('');
    setResultat(null);

    if (!/^\d{4}$/.test(form.tel_4_derniers)) {
      setErreur('Les 4 derniers chiffres du téléphone doivent être 4 chiffres.');
      return;
    }

    setChargement(true);
    try {
      const res = await fetch(`${API}/api/public/contrats/recherche`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setErreur(data.message || 'Aucune demande trouvée avec ces informations.');
      } else {
        setResultat(data);
      }
    } catch {
      setErreur('Erreur de connexion. Réessayez.');
    } finally {
      setChargement(false);
    }
  }

  return (
    <div className="rc-page">
      {/* En-tête */}
      <div className="rc-header">
        {settings.logo_url ? (
          <img
            src={settings.logo_url}
            alt={settings.nom_entreprise || 'Escamel'}
            className="rc-logo"
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="rc-logo-texte">ESCAMEL</div>
        )}
        <h1 className="rc-titre">Retrouver ma demande</h1>
        <p className="rc-sous-titre">
          Entrez vos informations pour accéder à votre soumission corporative.
        </p>
      </div>

      {/* Formulaire */}
      <div className="rc-main">
        <form className="rc-form-card" onSubmit={handleSubmit}>
          <div className="rc-field">
            <label className="rc-label">Nom de famille *</label>
            <input
              type="text"
              name="nom_famille"
              className="rc-input"
              placeholder="Votre nom de famille"
              value={form.nom_famille}
              onChange={handleChange}
              required
              autoComplete="family-name"
            />
          </div>

          <div className="rc-field">
            <label className="rc-label">4 derniers chiffres de votre téléphone *</label>
            <input
              type="text"
              name="tel_4_derniers"
              className="rc-input"
              placeholder="ex: 4321"
              value={form.tel_4_derniers}
              onChange={handleChange}
              maxLength={4}
              pattern="[0-9]{4}"
              inputMode="numeric"
              required
            />
            <p className="rc-field-note">
              Les 4 derniers chiffres du numéro fourni lors de votre demande
            </p>
          </div>

          <div className="rc-field">
            <label className="rc-label">Adresse courriel *</label>
            <input
              type="email"
              name="courriel"
              className="rc-input"
              placeholder="courriel@entreprise.com"
              value={form.courriel}
              onChange={handleChange}
              required
              autoComplete="email"
            />
          </div>

          {erreur && (
            <div className="rc-erreur">
              {erreur}
              {' '}
              <a href="mailto:info@escamel.ca">Contactez-nous à info@escamel.ca</a>
            </div>
          )}

          <button type="submit" className="rc-btn-submit" disabled={chargement}>
            {chargement ? (
              <span className="rc-spinner" />
            ) : (
              <>
                <Icon name="recherche" size={15} weight="bold" />
                Rechercher
              </>
            )}
          </button>
        </form>

        {/* Résultats */}
        {resultat && (
          <div className="rc-resultats">
            <h2 className="rc-resultats-titre">
              Bonjour {resultat.client.prenom} — voici vos demandes
            </h2>
            {resultat.contrats.map(c => (
              <ContratCard key={c.id} contrat={c} />
            ))}
          </div>
        )}

        {/* Lien bas de page */}
        <div className="rc-lien-bas">
          Vous avez une soumission pour un événement public ?{' '}
          <a href="/commande">Retrouver ma commande →</a>
        </div>
      </div>
    </div>
  );
}
